import { type FormEvent, type KeyboardEvent, useRef, useState } from "react";
import { type ChannelDTO, describeRecurrence, type LabelDTO, nowInZone, parseQuickAdd, parseRRule, type PersonalListDTO, type QuickToken, type TaskDTO, type TaskStatus, type UserDTO } from "@justone/shared";
import { errorMessage } from "../api/http";
import { useChannels, useUsers } from "../api/queries";
import { type CreateTaskBody, useCreateTask, useLabelMutations, useLabels, useLists } from "../api/tasks";
import { formatClock, formatDueDate, PRIORITY_NAMES } from "../lib/tasks";
import { useOpenTask } from "./TaskBits";
import { useToast } from "./ui";

export interface QuickAddDefaults {
  channelId?: number;
  listId?: number | null;
  dueDate?: string;
  labelIds?: number[];
  parentId?: number;
  status?: TaskStatus;
}

interface Props {
  defaults?: QuickAddDefaults;
  initialText?: string;
  sourceMessageId?: number;
  /** 위키 체크리스트 줄에서 만들 때 그 페이지 (줄이 할일 줄로 바뀐다) */
  wikiPageId?: number;
  /** 채널 메시지에서 만들 때: "내 할일로" 바꿀 수 있다 */
  allowPersonal?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  /** 한 줄짜리 (목록 안) */
  inline?: boolean;
  onDone?: (task: TaskDTO) => void;
  onCancel?: () => void;
}

type Place = { kind: "channel"; id: number; name: string } | { kind: "list"; id: number; name: string } | { kind: "inbox" };

interface Ignored {
  start: number;
  text: string;
}

export interface QuickAddData {
  lists: PersonalListDTO[];
  channels: ChannelDTO[];
  users: UserDTO[];
  labels: LabelDTO[];
}

export function useQuickAddData(): QuickAddData {
  const { data: lists = [] } = useLists();
  const { data: channels = [] } = useChannels();
  const { data: users = [] } = useUsers();
  const { data: labels = [] } = useLabels();
  return { lists, channels, users, labels };
}

export type QuickAddPlan = ReturnType<typeof planQuickAdd>;

/** 빠른 입력 글을 풀어 어디에 무엇을 만들지 정한다 (입력창과 위키 체크리스트 한꺼번에 만들기가 같이 쓴다) */
export function planQuickAdd(text: string, data: QuickAddData, defaults: QuickAddDefaults, opts: { personal?: boolean; ignore?: number[] } = {}) {
  const { lists, channels, users, labels } = data;
  const writable = channels.filter((c) => c.joined && !c.archived);
  const now = nowInZone();
  const parsed = parseQuickAdd(text, {
    today: now.date,
    nowMinutes: now.minutes,
    ignore: opts.ignore ?? [],
    isTarget: (name) => lists.some((l) => l.name === name) || writable.some((c) => c.name === name.toLowerCase()),
    isUser: (name) => users.some((u) => u.active && (u.username === name.toLowerCase() || u.displayName === name)),
  });

  // 어디에 만들지: #이름(목록 먼저, 다음 채널) > 화면 기본값
  let place: Place;
  const named = parsed.target;
  const list = named ? lists.find((l) => l.name === named) : undefined;
  const channel = named && !list ? writable.find((c) => c.name === named.toLowerCase()) : undefined;
  if (defaults.parentId) place = { kind: "inbox" };
  else if (list) place = { kind: "list", id: list.id, name: list.name };
  else if (channel) place = { kind: "channel", id: channel.id, name: channel.name };
  else if (defaults.channelId && !opts.personal) place = { kind: "channel", id: defaults.channelId, name: channels.find((c) => c.id === defaults.channelId)?.name ?? "" };
  else if (defaults.listId) place = { kind: "list", id: defaults.listId, name: lists.find((l) => l.id === defaults.listId)?.name ?? "" };
  else place = { kind: "inbox" };
  const team = place.kind === "channel" || (!!defaults.parentId && !!defaults.channelId);

  const assignee = parsed.assignee ? users.find((u) => u.active && (u.username === parsed.assignee!.toLowerCase() || u.displayName === parsed.assignee)) : undefined;
  const labelPlan = parsed.labels.map((name) => {
    const found = team ? labels.find((l) => l.scope === "shared" && l.name === name) : (labels.find((l) => l.scope === "personal" && l.name === name) ?? labels.find((l) => l.scope === "shared" && l.name === name));
    return { name, id: found?.id ?? null };
  });
  const newLabels = labelPlan.filter((l) => l.id === null);
  return { parsed, named, place, team, assignee, labelPlan, newLabels };
}

/** 계획대로 할일을 만든다 (새 라벨도 만든다) */
export function useCreateFromPlan() {
  const create = useCreateTask();
  const labelOps = useLabelMutations();
  const run = async (plan: QuickAddPlan, defaults: QuickAddDefaults, extra: { silent?: boolean; skipNewLabels?: boolean; sourceMessageId?: number; wikiPageId?: number } = {}) => {
    const { parsed, place, team, assignee, labelPlan } = plan;
    const labelIds = [...(defaults.labelIds ?? [])];
    for (const l of labelPlan) {
      if (l.id !== null) labelIds.push(l.id);
      else if (!extra.skipNewLabels) labelIds.push((await labelOps.create.mutateAsync({ name: l.name, scope: team ? "shared" : "personal" })).id);
    }
    const body: CreateTaskBody = {
      title: parsed.title,
      labelIds: [...new Set(labelIds)],
      dueDate: parsed.dueDate ?? defaults.dueDate ?? null,
      dueTime: parsed.dueTime,
      rrule: parsed.rrule,
      repeatFromCompletion: parsed.repeatFromCompletion,
      sourceMessageId: extra.sourceMessageId,
      wikiPageId: extra.wikiPageId,
      status: defaults.status,
    };
    if (parsed.priority) body.priority = parsed.priority;
    if (defaults.parentId) body.parentId = defaults.parentId;
    else if (place.kind === "channel") {
      body.channelId = place.id;
      body.silent = !!extra.silent;
    } else if (place.kind === "list") body.listId = place.id;
    if (team && assignee) body.assigneeId = assignee.id;
    return create.mutateAsync(body);
  };
  return { run, busy: create.isPending || labelOps.create.isPending };
}

/**
 * 빠른 입력: "내일 오후 3시 견적서 보내기 #영업 @minsu +급함 !1".
 * 입력하는 동안 인식한 낱말에 색을 칠하고 아래에 풀이를 보여준다. 풀이 칩을 누르면 그 낱말을 글자로 되돌린다.
 */
export function QuickAdd({ defaults = {}, initialText = "", sourceMessageId, wikiPageId, allowPersonal, autoFocus, placeholder, inline, onDone, onCancel }: Props) {
  const [text, setText] = useState(initialText);
  const [ignored, setIgnored] = useState<Ignored[]>([]);
  const [personal, setPersonal] = useState(false);
  const [silent, setSilent] = useState(false);
  const [skipNewLabels, setSkipNewLabels] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const data = useQuickAddData();
  const creator = useCreateFromPlan();
  const toast = useToast();
  const openTask = useOpenTask();

  // 낱말 위치가 그대로일 때만 해제를 유지한다 (앞을 고치면 다시 인식)
  const ignore = ignored.filter((x) => text.slice(x.start, x.start + x.text.length) === x.text).map((x) => x.start);
  const plan = planQuickAdd(text, data, defaults, { personal, ignore });
  const { parsed, named, place, team, assignee, labelPlan, newLabels } = plan;

  const tokenLabel = (tk: QuickToken): string => {
    switch (tk.kind) {
      case "date":
        return parsed.dueDate ? formatDueDate(parsed.dueDate) : tk.text;
      case "time":
        return parsed.dueTime ? formatClock(parsed.dueTime) : tk.text;
      case "recur": {
        const r = parsed.rrule ? parseRRule(parsed.rrule) : null;
        return r ? `${describeRecurrence(r, parsed.repeatFromCompletion)} · 다음 ${formatDueDate(parsed.dueDate!)}` : tk.text;
      }
      case "target":
        return place.kind === "list" ? `목록 ${place.name}` : place.kind === "channel" ? `#${place.name} 팀 할일` : tk.text;
      case "assignee":
        return team ? `담당 ${assignee?.displayName ?? tk.text}` : "담당자는 채널 할일에만";
      case "label":
        return labelPlan.find((l) => `+${l.name}` === tk.text)?.id === null ? `${tk.text} (새 라벨)` : tk.text;
      case "priority":
        return PRIORITY_NAMES[Number(tk.text.slice(1))] ?? tk.text;
    }
  };

  const dismiss = (tk: QuickToken) => {
    setIgnored((xs) => [...xs, { start: tk.start, text: tk.text }]);
    inputRef.current?.focus();
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!parsed.title) {
      setError("할일 제목을 입력하세요.");
      return;
    }
    setError(null);
    try {
      const task = await creator.run(plan, defaults, { silent, skipNewLabels, sourceMessageId, wikiPageId });
      setText("");
      setIgnored([]);
      if (onDone) onDone(task);
      else toast(`T-${task.id} "${task.title}"을(를) 추가했습니다.`, { label: "열기", run: () => openTask(task.id) });
      if (inline) inputRef.current?.focus();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel?.();
    }
  };

  const active = parsed.tokens;
  const busy = creator.busy;

  return (
    <form className={`quick-add${inline ? " inline" : ""}`} onSubmit={submit} aria-label="할일 빠르게 추가">
      <div className="qa-field">
        <div className="qa-backdrop" ref={backdropRef} aria-hidden="true">
          {highlight(text, active)}
        </div>
        <input
          ref={inputRef}
          className="qa-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          onScroll={(e) => {
            if (backdropRef.current) backdropRef.current.scrollLeft = e.currentTarget.scrollLeft;
          }}
          placeholder={placeholder ?? "예: 내일 오후 3시 견적서 보내기 #영업 @민수 +급함 !1"}
          aria-label="할일"
          aria-describedby="qa-help"
          autoFocus={autoFocus}
          autoComplete="off"
          enterKeyHint="done"
          maxLength={500}
        />
      </div>
      {(active.length > 0 || error) && (
        <div className="qa-chips" aria-live="polite">
          {active.map((tk) => (
            <button key={`${tk.start}-${tk.kind}`} type="button" className={`qa-chip tk-${tk.kind}${tk.kind === "assignee" && !team ? " off" : ""}`} onClick={() => dismiss(tk)} title="눌러서 글자로 두기">
              {tokenLabel(tk)}
              <span aria-hidden="true">×</span>
              <span className="sr-only">(해제)</span>
            </button>
          ))}
          {error && <span className="form-error qa-error">{error}</span>}
        </div>
      )}
      {newLabels.length > 0 && (
        <label className="qa-option">
          <input type="checkbox" checked={!skipNewLabels} onChange={(e) => setSkipNewLabels(!e.target.checked)} />
          {team ? "공용" : "개인"} 라벨 {newLabels.map((l) => `+${l.name}`).join(", ")}을(를) 새로 만들기
        </label>
      )}
      <div className="qa-foot">
        <span id="qa-help" className="qa-help">
          {defaults.parentId ? "하위 할일" : place.kind === "channel" ? `#${place.name} 보드에 올리고 채널에 카드를 남깁니다` : place.kind === "list" ? `목록 ${place.name}` : "받은함"}
          {defaults.dueDate && !parsed.dueDate && <> · 마감 {formatDueDate(defaults.dueDate)}</>}
          {parsed.title && <> · 제목: {parsed.title}</>}
        </span>
        {allowPersonal && defaults.channelId && !named && (
          <label className="qa-option">
            <input type="checkbox" checked={personal} onChange={(e) => setPersonal(e.target.checked)} />내 개인 할일로
          </label>
        )}
        {place.kind === "channel" && !defaults.parentId && (
          <label className="qa-option" title="채널에 알림 없이 카드를 올립니다">
            <input type="checkbox" checked={silent} onChange={(e) => setSilent(e.target.checked)} />
            조용히 올리기
          </label>
        )}
        <div className="qa-buttons">
          {onCancel && (
            <button type="button" className="btn btn-sm" onClick={onCancel}>
              취소
            </button>
          )}
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !text.trim()}>
            추가
          </button>
        </div>
      </div>
    </form>
  );
}

/** 입력창 뒤에 깔리는 색칠 (글자는 투명, 배경만 보인다) */
function highlight(text: string, tokens: QuickToken[]) {
  const out = [];
  let at = 0;
  for (const tk of [...tokens].sort((a, b) => a.start - b.start)) {
    if (tk.start > at) out.push(<span key={`t${at}`}>{text.slice(at, tk.start)}</span>);
    out.push(
      <mark key={`m${tk.start}`} className={`tk-${tk.kind}`}>
        {text.slice(tk.start, tk.end)}
      </mark>,
    );
    at = tk.end;
  }
  out.push(<span key="end">{text.slice(at) || " "}</span>);
  return out;
}
